import { SHORT_DATE_TIME_FORMAT } from './constants';
import { format } from 'date-fns';
import { camelCase, isArray, isDate, isNaN, isObject, snakeCase, transform } from 'lodash';
import { getDateLocale } from './localization';

/**
 * Returns the same array of objects with each tsStart and tsEnd parsed as Date javascript objects
 * @param {Array.<Object>|Object} data
 * @param {string} data.tsStart
 * @param {string} data.tsEnd
 * @returns {Array.<Object>}
 */
export function parseTsStartAndTsEnd(data) {
  let isArgumentArray = true;
  if (!(data instanceof Array)) {
    data = [data];
    isArgumentArray = false;
  }

  const result = data?.map((obj) => {
    return {
      ...obj,
      tsStart: new Date(obj.tsStart),
      tsEnd: obj.tsEnd ? new Date(obj.tsEnd) : null,
    };
  });

  if (!isArgumentArray && result?.length > 0) {
    return result[0];
  }

  return result;
}

/**
 * Returns a string representation of a given date. Returns '-' for null.
 * @param {Date|string|number} dt Date object or number or string representing a date
 * @param dtFormat the format of the date string to be returned. Default 'yyyy-MM-dd HH:mm'
 * @returns {string} String representation of the date.
 */
export function representDate(dt, dtFormat = SHORT_DATE_TIME_FORMAT) {
  if (!dt) {
    return '-';
  }

  return format(new Date(dt), dtFormat, {
    locale: getDateLocale(),
  });
}

/**
 * Returns a string in which an energy is represented with an energy unit.
 * @param {number} num representing energy
 * @returns {string} formatted number with unit
 */
export function formatEnergyWithUnit(num) {
  if (typeof num === 'number' && !isNaN(num)) {
    return Math.abs(Math.round(num)) > 999 ? (num / 1000).toFixed(1) + ' MWh' : num.toFixed() + ' kWh';
  } else {
    return '- kWh';
  }
}

/**
 * Returns a string with a percentage value.
 * @param {number} num representing a percentage
 * @returns {string} formatted number with unit
 */
export function formatPercentage(num, decimals = 1) {
  if (typeof num === 'number' && !isNaN(num)) {
    return `${Math.round(num * 100, decimals)} %`;
  } else {
    return '- %';
  }
}

// ---------------------------------------------------------------------------------------------------------------------
// Aggregator formatters
// ---------------------------------------------------------------------------------------------------------------------

export function formatCoordinateAsPoint({ lat, lng }) {
  if (!lng || !lat) {
    return;
  }

  return { type: 'Point', coordinates: [lng, lat] };
}

export const buildGraphConfigKey = (field, agg) => {
  return field + '_' + agg;
};

/**
 * Recursively transform object to camel-cased keys
 **/
export const keysToCamelCase = (obj) => {
  if (!obj) {
    return;
  }
  return transform(obj, (acc, value, key, target) => {
    const camelKey = isArray(target) ? key : camelCase(key);

    acc[camelKey] = isObject(value) && !isDate(value) ? keysToCamelCase(value) : value;
  });
};

export const keysToSnakeCase = (obj) => {
  if (!obj) {
    return;
  }
  return transform(obj, (acc, value, key, target) => {
    const snakeKey = isArray(target) ? key : snakeCase(key);

    acc[snakeKey] = isObject(value) && !isDate(value) ? keysToSnakeCase(value) : value;
  });
};

//Format with dot notation every 3 digits - design choice
export const formatNumber = (number) => {
  if (!typeof number === 'Number') {
    return number;
  }
  return new Intl.NumberFormat('de-DE').format(number);
};
